import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore";
import { SliderThumb } from "./SliderThumb";
import { TimeMarkers } from "./TimeMarkers";
import * as Slider from "@opstel/radix/Slider";
import { DateTime, Duration } from "luxon";
import React, { useCallback, useMemo, useState } from "react";

/**
 * The TimeSlider allows the user to select a time. The slider is horizontal and the user can slide the thumb to select a time.
 */
export function TimeSlider(): React.JSX.Element {
  const [isPointerDown, setIsPointerDown] = useState(false);
  const setDateTime = useSelectedTimeStore((state) => state.setDateTime);
  const selectedTime = useSelectedTimeStore((state) => state.selectedTime)!;
  const scenarioInterval = useSelectedTimeStore((state) => state.scenarioInterval);

  const hourMarks: DateTime[] = useMemo(() => {
    if (!scenarioInterval) {
      return [];
    }
    const totalHours = scenarioInterval.end.diff(scenarioInterval.start).as("hours") + 1;
    return Array.from({ length: totalHours }, (_x, i) => {
      return scenarioInterval.start.plus(Duration.fromObject({ hours: i }));
    });
  }, [scenarioInterval]);

  const handleSliderThumbPointerDown = useCallback(() => {
    setIsPointerDown(true);
  }, []);

  const handleSliderThumbPointerUp = useCallback(() => {
    setIsPointerDown(false);
  }, []);

  const handleValueChanged = useCallback(
    ([minutes]: [number]) =>
      scenarioInterval &&
      setDateTime(scenarioInterval.start.plus(Duration.fromObject({ minutes }))),
    [setDateTime, scenarioInterval],
  );

  return (
    <div className="h-[40px] w-[100%] bg-default">
      <Slider.Root
        data-testid="epd-time-slider"
        className="relative flex h-[60px] touch-none items-center outline-0 select-none"
        value={scenarioInterval ? [selectedTime.diff(scenarioInterval.start).as("minutes")] : []}
        max={(hourMarks.length - 1) * 60}
        onValueChange={handleValueChanged}
        step={1}
      >
        <Slider.Track className="relative h-[5px] grow rounded-full bg-emphasis-primary">
          <Slider.Range className="absolute h-full rounded-full bg-emphasis-primary" />
        </Slider.Track>
        {scenarioInterval && <TimeMarkers hourMarks={hourMarks} />}
        <SliderThumb
          data-testid="epd-time-slider-thumb"
          isPointerDown={isPointerDown}
          aria-label="Selected time"
          onPointerDown={handleSliderThumbPointerDown}
          onPointerUp={handleSliderThumbPointerUp}
        />
      </Slider.Root>
    </div>
  );
}
